import { useState, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { leads, deals, relationshipManagers, properties } from '@/mocks/dashboard';
import { Link } from 'react-router-dom';

type AnalyticsTab = 'overview' | 'pipeline' | 'performance' | 'geography';

function formatINR(val: number): string {
  if (val >= 10000000) return '\u20B9' + (val / 10000000).toFixed(1) + 'Cr';
  if (val >= 100000) return '\u20B9' + (val / 100000).toFixed(1) + 'L';
  if (val >= 1000) return '\u20B9' + (val / 1000).toFixed(0) + 'K';
  return '\u20B9' + val.toFixed(0);
}

function MiniBarChart({ data, labels, color, height = 100 }: { data: number[]; labels: string[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const barW = 80 / data.length;
  const gap = 20 / data.length;
  const colors: Record<string, string> = {
    accent: 'oklch(var(--accent-500))', primary: 'oklch(var(--primary-500))',
    secondary: 'oklch(var(--secondary-500))', teal: '#14b8a6', rose: '#f43f5e',
  };
  return (
    <svg viewBox="0 0 100 100" style={{ height }} preserveAspectRatio="none" className="w-full">
      {data.map((v, i) => {
        const h = (v / max) * 75;
        const x = 10 + i * (barW + gap);
        return (
          <g key={i}>
            <rect x={x} y={90 - h} width={barW} height={h} rx="1.5" fill={colors[color] || colors.accent} opacity={0.7} />
            <text x={x + barW / 2} y={97} textAnchor="middle" fontSize={5} fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

function AreaChart({ data, labels, color, height = 120 }: { data: number[]; labels: string[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const stepX = 80 / (data.length - 1);
  const pts = data.map((v, i) => `${10 + i * stepX},${90 - (v / max) * 75}`).join(' ');
  const area = `M10,90 ${pts} L${10 + (data.length - 1) * stepX},90 Z`;
  const colorMap: Record<string, { fill: string; stroke: string }> = {
    accent: { fill: 'oklch(var(--accent-500) / 0.2)', stroke: 'oklch(var(--accent-500))' },
    primary: { fill: 'oklch(var(--primary-500) / 0.2)', stroke: 'oklch(var(--primary-500))' },
    teal: { fill: 'rgba(20,184,166,0.15)', stroke: '#14b8a6' },
    rose: { fill: 'rgba(244,63,94,0.15)', stroke: '#f43f5e' },
  };
  const c = colorMap[color] || colorMap.accent;
  return (
    <svg viewBox="0 0 100 100" style={{ height }} preserveAspectRatio="none" className="w-full">
      <path d={area} fill={c.fill} />
      <polyline points={pts} fill="none" stroke={c.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((v, i) => (
        <g key={i}>
          <circle cx={10 + i * stepX} cy={90 - (v / max) * 75} r="2" fill={c.stroke} />
          <text x={10 + i * stepX} y={97} textAnchor="middle" fontSize={4.5} fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
        </g>
      ))}
    </svg>
  );
}

function HorizontalBar({ label, value, max, color, displayValue }: { label: string; value: number; max: number; color: string; displayValue: string }) {
  const pct = Math.round((value / max) * 100);
  const barColors: Record<string, string> = {
    accent: 'bg-accent-500', primary: 'bg-primary-500', secondary: 'bg-secondary-500',
    teal: 'bg-teal-500', rose: 'bg-rose-500',
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-foreground-500">{label}</span>
        <span className="text-xs font-semibold text-foreground-950">{displayValue}</span>
      </div>
      <div className="h-2 bg-background-200/60 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColors[color] || barColors.accent}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function CRMAnalyticsPage() {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  // Compute analytics from mock data
  const totalLeads = leads.length;
  const converted = leads.filter(l => l.status === 'Converted' || l.status === 'Closed Won').length;
  const qualified = leads.filter(l => l.status === 'Qualified').length;
  const avgScore = Math.round(leads.reduce((s, l) => s + l.score, 0) / totalLeads);
  const conversionRate = Math.round((converted / totalLeads) * 100 * 10) / 10;
  const totalPipelineValue = deals.reduce((sum, d) => sum + d.dealValue, 0);
  const closedValue = deals.filter(d => d.stage === 'Closed').reduce((sum, d) => sum + d.dealValue, 0);

  // Source breakdown
  const sourceBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach(l => { map[l.source] = (map[l.source] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, []);

  // Customer type breakdown
  const typeBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach(l => { map[l.customerType] = (map[l.customerType] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, []);

  // Budget distribution
  const budgetBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach(l => { map[l.budgetRange] = (map[l.budgetRange] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, []);

  // Property type demand
  const propertyTypeDemand = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach(l => {
      const prop = properties.find(p => l.propertyInterest.toLowerCase().includes(p.type.toLowerCase()));
      const type = prop?.type || 'Commercial';
      map[type] = (map[type] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, []);

  // Monthly trend data (mock 12-month)
  const monthlyLeads = [42, 48, 55, 51, 62, 68, 72, 65, 78, 82, 88, 94];
  const monthlyRevenue = [2.8, 3.1, 3.5, 3.2, 4.0, 4.5, 5.2, 4.8, 5.5, 5.0, 5.8, 6.2];
  const shortMonths = ['J','F','M','A','M','J','J','A','S','O','N','D'];

  // Pipeline stages
  const pipelineStages = [
    { stage: 'New Lead', count: leads.filter(l => l.status === 'New Lead').length, color: 'bg-blue-500' },
    { stage: 'In Progress', count: leads.filter(l => l.status === 'In Progress').length, color: 'bg-accent-500' },
    { stage: 'Contacted', count: leads.filter(l => l.status === 'Contacted').length, color: 'bg-accent-500' },
    { stage: 'Qualified', count: leads.filter(l => l.status === 'Qualified').length, color: 'bg-teal-500' },
    { stage: 'Converted', count: leads.filter(l => l.status === 'Converted' || l.status === 'Closed Won').length, color: 'bg-green-500' },
    { stage: 'Closed Lost', count: leads.filter(l => l.status === 'Closed Lost').length, color: 'bg-rose-500' },
  ];
  const maxPipelineCount = Math.max(...pipelineStages.map(s => s.count), 1);

  // RM Performance
  const rmPerf = relationshipManagers.map(rm => ({
    name: rm.name,
    leads: leads.filter(l => l.assignedRM === rm.name).length,
    converted: leads.filter(l => l.assignedRM === rm.name && (l.status === 'Converted' || l.status === 'Closed Won')).length,
    avgScore: Math.round(leads.filter(l => l.assignedRM === rm.name).reduce((s, l) => s + l.score, 0) / Math.max(leads.filter(l => l.assignedRM === rm.name).length, 1)),
    pipelineValue: deals.filter(d => d.assignedRM === rm.name).reduce((s, d) => s + d.dealValue, 0),
    convRate: rm.conversionRate,
  }));

  // City demand
  const cityDemand = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach(l => {
      const prop = properties.find(p => l.propertyInterest.toLowerCase().includes(p.title.toLowerCase().split(' ')[0]));
      const city = prop?.city || 'Bangalore';
      map[city] = (map[city] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, []);

  const tabs: { key: AnalyticsTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { key: 'pipeline', label: 'Pipeline', icon: 'ri-kanban-view' },
    { key: 'performance', label: 'RM Performance', icon: 'ri-team-line' },
    { key: 'geography', label: 'Geography', icon: 'ri-map-pin-line' },
  ];

  const colors = ['bg-accent-500', 'bg-primary-500', 'bg-secondary-500', 'bg-teal-500', 'bg-rose-500', 'bg-blue-500'];

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">CRM Analytics</h1>
            <p className="text-xs text-foreground-500">Deep insights into leads, pipeline, and performance</p>
          </div>
          <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1">
            {(['7d','30d','90d','1y'] as const).map(d => (
              <button key={d} onClick={() => setDateRange(d)} className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap cursor-pointer transition-all ${dateRange === d ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'}`}>
                {d === '7d' ? 'Last 7D' : d === '30d' ? 'Last 30D' : d === '90d' ? 'Last 90D' : 'Last Year'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Leads', value: totalLeads.toString(), sub: '+24% vs last period', icon: 'ri-user-search-line', color: 'accent', positive: true },
            { label: 'Conversion Rate', value: conversionRate + '%', sub: '-1.2% vs last period', icon: 'ri-line-chart-line', color: 'teal', positive: false },
            { label: 'Pipeline Value', value: formatINR(totalPipelineValue), sub: formatINR(closedValue) + ' closed', icon: 'ri-money-rupee-circle-line', color: 'primary', positive: true },
            { label: 'Avg Lead Score', value: avgScore.toString(), sub: `${qualified} qualified`, icon: 'ri-bar-chart-line', color: 'secondary', positive: true },
          ].map(kpi => (
            <div key={kpi.label} className="rounded-xl border bg-white border-background-200/60 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-foreground-500 uppercase tracking-wider">{kpi.label}</span>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  kpi.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                  kpi.color === 'teal' ? 'bg-teal-500/15 text-teal-400' :
                  kpi.color === 'primary' ? 'bg-primary-500/15 text-primary-700' :
                  'bg-secondary-500/15 text-secondary-700'
                }`}><i className={`${kpi.icon} text-xs`} /></span>
              </div>
              <p className="font-heading text-xl font-bold text-foreground-950">{kpi.value}</p>
              <p className={`text-[10px] mt-1 ${kpi.positive ? 'text-teal-400' : 'text-rose-400'}`}>{kpi.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 w-fit flex-wrap">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-all ${activeTab === tab.key ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'}`}>
              <i className={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Monthly Leads Trend */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground-950">Monthly Lead Volume</h3>
                  <span className="text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">+24% YoY</span>
                </div>
                <AreaChart data={monthlyLeads} labels={shortMonths} color="accent" height={120} />
              </div>

              {/* Revenue Trend */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground-950">Revenue Trend (₹L)</h3>
                  <span className="text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">+18% YoY</span>
                </div>
                <AreaChart data={monthlyRevenue} labels={shortMonths} color="primary" height={120} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Lead Sources */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Lead Sources</h3>
                <div className="space-y-2.5">
                  {sourceBreakdown.map(([source, count], i) => (
                    <HorizontalBar key={source} label={source} value={count} max={sourceBreakdown[0][1]} color={['accent','primary','secondary','teal','rose'][i % 5]} displayValue={`${count} (${Math.round((count/totalLeads)*100)}%)`} />
                  ))}
                </div>
              </div>

              {/* Customer Types */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Customer Types</h3>
                <div className="space-y-3 mt-2">
                  {typeBreakdown.map(([type, count], i) => {
                    const pct = Math.round((count / totalLeads) * 100);
                    return (
                      <div key={type} className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-sm ${colors[i % colors.length]} flex-shrink-0`} />
                        <span className="text-xs text-foreground-500 flex-1">{type}</span>
                        <span className="text-xs font-semibold text-foreground-950">{count}</span>
                        <div className="w-14 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-foreground-500 w-7 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5">
                  <h4 className="text-xs font-semibold text-foreground-950 mb-3">Budget Distribution</h4>
                  <div className="space-y-2">
                    {budgetBreakdown.map(([budget, count], i) => (
                      <HorizontalBar key={budget} label={budget} value={count} max={budgetBreakdown[0][1]} color={['primary','accent','teal','secondary','rose'][i % 5]} displayValue={`${count} leads`} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Property Type Demand */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Property Type Demand</h3>
                <div className="space-y-2.5">
                  {propertyTypeDemand.map(([type, count], i) => (
                    <HorizontalBar key={type} label={type} value={count} max={propertyTypeDemand[0][1]} color={['accent','primary','secondary','teal','rose'][i % 5]} displayValue={`${count} leads`} />
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-background-200/60">
                  <h4 className="text-xs font-semibold text-foreground-950 mb-3">Deal Pipeline Summary</h4>
                  {[
                    { label: 'Total Pipeline', value: formatINR(totalPipelineValue), color: 'text-foreground-950' },
                    { label: 'Closed Value', value: formatINR(closedValue), color: 'text-teal-400' },
                    { label: 'Active Deals', value: deals.filter(d => d.stage !== 'Closed').length.toString(), color: 'text-accent-700' },
                    { label: 'Avg Deal Size', value: formatINR(totalPipelineValue / Math.max(deals.length, 1)), color: 'text-foreground-950' },
                  ].map(s => (
                    <div key={s.label} className="flex items-center justify-between py-1.5 border-b border-background-200/50 last:border-0">
                      <span className="text-xs text-foreground-500">{s.label}</span>
                      <span className={`text-xs font-bold ${s.color}`}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pipeline Tab */}
        {activeTab === 'pipeline' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Funnel */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Conversion Funnel</h3>
                <div className="space-y-2">
                  {pipelineStages.map(stage => {
                    const pct = Math.round((stage.count / totalLeads) * 100);
                    return (
                      <div key={stage.stage} className="flex items-center gap-3">
                        <span className="text-xs text-foreground-500 w-24 text-right flex-shrink-0">{stage.stage}</span>
                        <div className="flex-1 h-6 bg-background-200/60 rounded overflow-hidden">
                          <div className={`h-full rounded ${stage.color} opacity-80 flex items-center justify-end pr-2`} style={{ width: `${Math.max(pct, 5)}%`, minWidth: stage.count > 0 ? '30px' : '0' }}>
                            {stage.count > 0 && <span className="text-[10px] font-semibold text-white">{stage.count}</span>}
                          </div>
                        </div>
                        <span className="text-[10px] text-foreground-500 w-8">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-background-200/60">
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Win Rate', value: conversionRate + '%', color: 'text-teal-400' },
                      { label: 'Avg Days/Stage', value: Math.round(leads.reduce((s, l) => s + l.daysInStage, 0) / totalLeads) + 'd', color: 'text-accent-700' },
                      { label: 'Qualified Rate', value: Math.round((qualified / totalLeads) * 100) + '%', color: 'text-primary-700' },
                    ].map(s => (
                      <div key={s.label} className="text-center p-3 rounded-lg bg-background-200">
                        <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-[10px] text-foreground-500 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Deal Pipeline Table */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground-950">Active Deals</h3>
                  <Link to="/dashboard/crm" className="text-[10px] font-semibold text-accent-700 hover:text-accent-300">Manage in CRM</Link>
                </div>
                <div className="space-y-2.5">
                  {deals.map(deal => (
                    <div key={deal.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-background-200 border border-background-200/50">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        deal.stage === 'Closed' ? 'bg-green-500/15 text-green-400' :
                        deal.stage.includes('Agreement') ? 'bg-teal-500/15 text-teal-400' :
                        deal.stage.includes('Proposal') ? 'bg-accent-100 text-accent-700' :
                        'bg-primary-500/15 text-primary-700'
                      }`}>
                        <i className={`text-xs ${deal.stage === 'Closed' ? 'ri-check-line' : deal.stage.includes('Agreement') ? 'ri-file-text-line' : 'ri-scales-line'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground-950 truncate">{deal.leadName}</p>
                        <p className="text-[10px] text-foreground-500 truncate">{deal.stage}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-bold text-foreground-950">{formatINR(deal.dealValue)}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <div className="w-10 h-1 bg-background-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${deal.probability >= 70 ? 'bg-teal-500' : deal.probability >= 40 ? 'bg-accent-500' : 'bg-rose-500'}`} style={{ width: `${deal.probability}%` }} />
                          </div>
                          <span className="text-[9px] font-semibold text-foreground-950">{deal.probability}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-background-200/60 flex items-center justify-between">
                  <span className="text-[10px] text-foreground-500">Total Pipeline</span>
                  <span className="text-sm font-bold text-accent-700">{formatINR(totalPipelineValue)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RM Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-4">
            {/* RM Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {rmPerf.map((rm, i) => (
                <div key={rm.name} className="rounded-xl border border-background-200/60 bg-white p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      i === 0 ? 'bg-accent-100 text-accent-700' : i === 1 ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                    }`}>{rm.name.split(' ').map(n => n[0]).join('')}</div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{rm.name}</p>
                      <p className="text-[10px] text-foreground-500">{rm.leads} leads assigned</p>
                    </div>
                    {i === 0 && <span className="ml-auto text-[9px] font-semibold text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded-full">Top RM</span>}
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { label: 'Total Leads', value: rm.leads.toString(), max: 20, color: 'accent' },
                      { label: 'Converted', value: rm.converted.toString(), max: 15, color: 'teal' },
                      { label: 'Conv. Rate', value: rm.convRate + '%', max: 30, color: 'primary' },
                    ].map(m => (
                      <div key={m.label} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-foreground-500">{m.label}</span>
                          <span className="text-[10px] font-semibold text-foreground-950">{m.value}</span>
                        </div>
                        <div className="h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${m.color === 'accent' ? 'bg-accent-500' : m.color === 'teal' ? 'bg-teal-500' : 'bg-primary-500'}`} style={{ width: `${Math.min((parseFloat(m.value) / m.max) * 100, 100)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-background-200/60 flex items-center justify-between">
                    <span className="text-[10px] text-foreground-500">Pipeline Value</span>
                    <span className="text-sm font-bold text-accent-700">{formatINR(rm.pipelineValue)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparative Table */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200/60">
                <h3 className="text-sm font-semibold text-foreground-950">RM Comparative Performance</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/60">
                      {['RM Name', 'Assigned Leads', 'Converted', 'Conv. Rate', 'Avg Score', 'Pipeline Value', 'Performance'].map(h => (
                        <th key={h} className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rmPerf.sort((a, b) => b.convRate - a.convRate).map((rm, idx) => (
                      <tr key={rm.name} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {idx === 0 && <i className="ri-medal-fill text-amber-500 text-xs" />}
                            <span className="text-sm font-medium text-foreground-950">{rm.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground-950">{rm.leads}</td>
                        <td className="px-4 py-3 text-sm text-foreground-950">{rm.converted}</td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-semibold ${rm.convRate >= 20 ? 'text-teal-400' : rm.convRate >= 15 ? 'text-accent-700' : 'text-foreground-500'}`}>{rm.convRate}%</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-14 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${rm.avgScore >= 70 ? 'bg-teal-500' : rm.avgScore >= 50 ? 'bg-accent-500' : 'bg-rose-500'}`} style={{ width: `${rm.avgScore}%` }} />
                            </div>
                            <span className="text-xs font-semibold text-foreground-950">{rm.avgScore}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-foreground-950">{formatINR(rm.pipelineValue)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <i key={i} className={`ri-star-fill text-xs ${i < Math.round(rm.convRate / 5) ? 'text-amber-400' : 'text-background-200'}`} />
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Geography Tab */}
        {activeTab === 'geography' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* City Demand */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Lead Demand by City</h3>
                <div className="space-y-3">
                  {cityDemand.map(([city, count], i) => (
                    <div key={city} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${
                        i === 0 ? 'bg-accent-100 text-accent-700' : i === 1 ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-500'
                      }`}>{i + 1}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-medium text-foreground-950">{city}</span>
                          <span className="text-xs font-semibold text-foreground-950">{count} leads</span>
                        </div>
                        <div className="h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${Math.round((count / cityDemand[0][1]) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Property Type by City */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Properties by City</h3>
                <div className="space-y-3">
                  {(() => {
                    const cityProps: Record<string, number> = {};
                    properties.forEach(p => { cityProps[p.city] = (cityProps[p.city] || 0) + 1; });
                    return Object.entries(cityProps).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([city, count], i) => (
                      <div key={city} className="flex items-center gap-3">
                        <i className="ri-map-pin-2-fill text-accent-700 text-xs flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs text-foreground-950">{city}</span>
                            <span className="text-xs font-semibold text-foreground-950">{count} properties</span>
                          </div>
                          <div className="h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${Math.round((count / properties.length) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                <div className="mt-5 pt-4 border-t border-background-200/60">
                  <h4 className="text-xs font-semibold text-foreground-950 mb-3">Top Demand Zones</h4>
                  <div className="space-y-1.5">
                    {[
                      { zone: 'South Bangalore (MG Road, Koramangala)', demand: 'High', color: 'text-teal-400 bg-teal-500/10' },
                      { zone: 'Mumbai Western Suburbs (Powai, Bhiwandi)', demand: 'High', color: 'text-teal-400 bg-teal-500/10' },
                      { zone: 'Delhi NCR (CP, Cyber City)', demand: 'Medium', color: 'text-accent-700 bg-accent-100' },
                      { zone: 'Pune IT Corridor (Hinjewadi)', demand: 'Medium', color: 'text-accent-700 bg-accent-100' },
                      { zone: 'Hyderabad (Gachibowli, Financial District)', demand: 'Growing', color: 'text-primary-700 bg-primary-100' },
                    ].map(z => (
                      <div key={z.zone} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white">
                        <span className="text-xs text-foreground-950">{z.zone}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${z.color}`}>{z.demand}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}